import React, { useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { FiZap, FiEdit3, FiImage, FiCalendar, FiSend, FiTrendingUp, FiTarget, FiLayers } from 'react-icons/fi';
import usePageMeta from '../shared/hooks/usePageMeta';
import { SUPPORT_EMAIL } from '../shared/constants/contactInfo';

const REDUCED_MOTION = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const stagger = REDUCED_MOTION
  ? {}
  : { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.2 } };

function AnimatedSection({ children, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  if (REDUCED_MOTION) {
    return <div ref={ref} className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

const WORKFLOW_STEPS = [
  {
    icon: <FiTarget />,
    title: 'Set Up Your Brand',
    description: 'Create a project and tell us about your business. Our AI analyzes your industry, keywords, and brand voice to build a personalized content profile.',
    detail: 'One-time setup that powers all future content generation.',
  },
  {
    icon: <FiTrendingUp />,
    title: 'Discover Trends',
    description: 'Our AI scans the web for trends relevant to your niche using Google Search grounding. Get trending topics with ready-made content angles.',
    detail: 'Fresh trends updated every 4 hours, tailored to your brand.',
  },
  {
    icon: <FiZap />,
    title: 'Choose Your Spark',
    description: 'Pick a trending topic, use a content stream template, or describe your own idea. This becomes the creative brief for your content.',
    detail: 'Start from inspiration or start from scratch — your choice.',
  },
  {
    icon: <FiEdit3 />,
    title: 'AI Crafts Your Content',
    description: 'Answer a few clarifying questions, then watch as AI generates a complete content plan with post copy optimized for your chosen platforms.',
    detail: 'Full creative control — edit, regenerate, or refine any part.',
  },
  {
    icon: <FiImage />,
    title: 'Generate Visuals',
    description: 'Our visual engine creates stunning images matched to your post. Choose from multiple art styles — from photorealistic to illustrated.',
    detail: 'Perfect aspect ratios for Stories, Reels, Feed posts, and more.',
  },
  {
    icon: <FiCalendar />,
    title: 'Schedule or Publish',
    description: 'Pick a date and time, or publish immediately. Content is pushed directly to your connected Facebook and Instagram pages.',
    detail: 'No downloading and re-uploading — one click from AI to live.',
  },
  {
    icon: <FiLayers />,
    title: 'Automate with Streams',
    description: 'Set up recurring content streams that automatically generate new posts on a schedule. Set it and forget it — your content engine runs itself.',
    detail: 'Batch processing runs overnight so content is ready each morning.',
  },
  {
    icon: <FiSend />,
    title: 'Scale & Iterate',
    description: 'Review published content, track what resonates, and refine your strategy. Each project builds on its own history for better results over time.',
    detail: 'Your AI gets smarter about your brand with every piece of content.',
  },
];

function HowItWorks() {
  usePageMeta(
    'How It Works',
    'See how NextAInsight takes you from brand setup to published social media content in minutes using AI-powered automation.'
  );

  return (
    <>
      {/* Hero */}
      <section className="relative pt-24 pb-16 px-4 sm:px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <AnimatedSection>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-slate-blue mb-6">The Workflow</span>
            <h1 className="text-4xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6 text-transparent bg-clip-text bg-gradient-to-b from-cream to-cream/50">
              From Idea to Published<br />in Minutes
            </h1>
            <p className="text-xl text-cream/60 max-w-2xl mx-auto leading-relaxed">
              NextAInsight's AI-powered pipeline handles every step of content creation — from trend discovery to visual generation to publishing.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 md:left-12 top-0 bottom-0 w-px bg-gradient-to-b from-slate-blue/30 via-slate-blue/10 to-transparent hidden md:block"></div>

            <div className="space-y-8">
              {WORKFLOW_STEPS.map((step, i) => (
                <motion.div
                  key={step.title}
                  {...stagger}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="relative flex gap-6 md:gap-10 group"
                >
                  {/* Step number + icon */}
                  <div className="shrink-0 relative z-10">
                    <div className="w-16 h-16 md:w-24 md:h-24 bg-warm-gray/30 backdrop-blur-md border border-white/5 rounded-2xl flex flex-col items-center justify-center group-hover:border-slate-blue/30 group-hover:bg-warm-gray/50 transition-all">
                      <span className="text-[10px] font-black text-cream/20 uppercase tracking-widest">{String(i + 1).padStart(2, '0')}</span>
                      <span className="text-slate-blue text-xl md:text-2xl mt-1">{step.icon}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pt-2 md:pt-4 pb-4">
                    <h3 className="text-xl md:text-2xl font-bold text-cream mb-2 group-hover:text-white transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-cream/60 leading-relaxed mb-2">{step.description}</p>
                    <p className="text-sm text-slate-blue/80 font-medium">{step.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4">
        <AnimatedSection>
          <div className="max-w-4xl mx-auto bg-warm-gray/30 backdrop-blur-xl rounded-[2.5rem] p-12 md:p-16 text-center border border-white/5 relative overflow-hidden">
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-6 text-cream">Want This For Your Brand?</h2>
              <p className="text-cream/60 text-lg mb-10 max-w-xl mx-auto">
                Tell us about your brand and we'll take it from there.
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-block px-10 py-5 bg-cream text-warm-dark text-sm font-black uppercase tracking-widest rounded-full hover:bg-white hover:scale-105 transition-all shadow-[0_0_30px_rgba(235,226,221,0.2)]"
              >
                Get In Touch
              </a>
            </div>
          </div>
        </AnimatedSection>
      </section>
    </>
  );
}

export default HowItWorks;
